import * as THREE from 'three'
import { LayerOrders } from '../../shared/shared'

/**
 * CombatText
 * ─────────────
 * Fire-and-forget floating combat text.  Single draw call via InstancedMesh.
 * All animation (arc + fade + pop) runs in the shader — JS only touches buffers
 * on spawn and cleanup, never per-frame.
 *
 *   const ct = new CombatText(client)
 *   ct.show({ text: 42, x, y })
 *   ct.show({ text: '+15', x, y, color: '#4f4' })
 *   ct.show({ text: 999, x, y, scale: 1.4, color: '#ff4444' })
 */

// ── atlas config ──

const ATLAS_W = 512
const INITIAL_ATLAS_H = 256

const SLOT_W = 256
const SLOT_H = 60
const SLOTS_PER_ROW = Math.floor(ATLAS_W / SLOT_W)

const FONT = 'bold 36px Pixel'

const OUTLINE_COLOR = '#000'
const OUTLINE_SIZE = 5

// ── particle config ──

const MAX_ACTIVE = 128
const LIFETIME = 0.7
const LAUNCH_SPEED = 4.0
const GRAVITY = 20.0
const LAUNCH_ANGLE_MIN = 70 // degrees from horizontal
const LAUNCH_ANGLE_MAX = 80
const RISE_SPEED = 2.0
const SPREAD = 0

const QUAD_W = 2.0
const QUAD_H = QUAD_W * (SLOT_H / SLOT_W)

export class ClientCombatText {
  constructor(client) {
    this.client = client

    // ── atlas ──
    this.atlasW = ATLAS_W
    this.atlasH = INITIAL_ATLAS_H
    this.canvas = document.createElement('canvas')
    this.canvas.width = this.atlasW
    this.canvas.height = this.atlasH
    this.ctx = this.canvas.getContext('2d')
    this.ctx.imageSmoothingEnabled = false

    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.magFilter = THREE.LinearFilter
    this.texture.minFilter = THREE.LinearFilter
    this.texture.generateMipmaps = false

    // ── glyph cache: "text|color" → { slotIdx, lastUsed } ──
    this._cache = new Map()
    this._slots = []
    this._freeSlots = []
    this._nextSlot = 0

    // ── active list (lightweight bookkeeping only) ──
    this._active = [] // { idx, startTime, lifetime }
    this._count = 0
    this._dirty = false

    // ── instanced mesh ──
    const geo = new THREE.PlaneGeometry(QUAD_W, QUAD_H)

    this._uvArr = new Float32Array(MAX_ACTIVE * 4)
    this._startPosArr = new Float32Array(MAX_ACTIVE * 2)
    this._startTimeArr = new Float32Array(MAX_ACTIVE)
    this._scaleArr = new Float32Array(MAX_ACTIVE)
    this._velArr = new Float32Array(MAX_ACTIVE * 2) // launch velocity (vx, vy)
    this._gravArr = new Float32Array(MAX_ACTIVE) // per-instance gravity (0 for rise style)

    geo.setAttribute('aUvRect', new THREE.InstancedBufferAttribute(this._uvArr, 4))
    geo.setAttribute('aStartPos', new THREE.InstancedBufferAttribute(this._startPosArr, 2))
    geo.setAttribute('aStartTime', new THREE.InstancedBufferAttribute(this._startTimeArr, 1))
    geo.setAttribute('aScale', new THREE.InstancedBufferAttribute(this._scaleArr, 1))
    geo.setAttribute('aVel', new THREE.InstancedBufferAttribute(this._velArr, 2))
    geo.setAttribute('aGravity', new THREE.InstancedBufferAttribute(this._gravArr, 1))

    const mat = this._createMaterial()
    this.mesh = new THREE.InstancedMesh(geo, mat, MAX_ACTIVE)
    this.mesh.frustumCulled = false
    this.mesh.count = 0
    this.mesh.renderOrder = LayerOrders.COMBAT_TEXT
    this.client.scene.add(this.mesh)
  }

  // ── material ──

  _createMaterial() {
    return new THREE.ShaderMaterial({
      uniforms: {
        uAtlas: { value: this.texture },
        uTime: { value: 0 },
        uLifetime: { value: LIFETIME },
      },
      vertexShader: /* glsl */ `
        precision highp float;

        attribute vec4 aUvRect;
        attribute vec2 aStartPos;
        attribute float aStartTime;
        attribute float aScale;
        attribute vec2 aVel;
        attribute float aGravity;

        uniform float uTime;
        uniform float uLifetime;

        varying vec2 vUv;
        varying float vOpacity;

        void main() {
          float age = uTime - aStartTime;
          float t = clamp(age / uLifetime, 0.0, 1.0);

          // uv
          vUv = vec2(
            aUvRect.x + uv.x * aUvRect.z,
            aUvRect.y + uv.y * aUvRect.w
          );

          // fade: arc style fades late, rise style fades with quadratic ease-out
          // use gravity as a proxy — if gravity > 0 it's an arc
          float isArc = step(0.01, aGravity);
          float arcFade = smoothstep(0.4, 1.0, t);
          float riseFade = t;
          float fadeT = mix(riseFade, arcFade, isArc);
          vOpacity = 1.0 - fadeT * fadeT;

          // pop: starts at 1.3x, settles to 1x quickly
          float pop = 1.0 + 0.3 * (1.0 - smoothstep(0.0, 0.15, t));

          // motion: p = p0 + v*t - 0.5*g*t^2
          // rise style: aVel = (0, riseSpeed), aGravity = 0 → straight up
          // arc style:  aVel = (vx, vy),       aGravity > 0 → parabola
          float dx = aVel.x * age;
          float dy = aVel.y * age - 0.5 * aGravity * age * age;

          vec3 pos = position * aScale * pop;
          pos.x += aStartPos.x + dx;
          pos.y += aStartPos.y + dy;

          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform sampler2D uAtlas;
        varying vec2 vUv;
        varying float vOpacity;
        void main() {
          vec4 c = texture2D(uAtlas, vUv);
          c.a *= vOpacity;
          if (c.a < 0.01) discard;
          gl_FragColor = c;
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    })
  }

  // ── atlas slot management ──

  _allocSlot() {
    if (this._freeSlots.length > 0) return this._freeSlots.pop()
    const idx = this._nextSlot++
    this._slots.push(null)
    const row = Math.floor(idx / SLOTS_PER_ROW)
    const needed = (row + 1) * SLOT_H
    if (needed > this.atlasH) this._growAtlas(needed)
    return idx
  }

  _evictLRU() {
    let oldest = Infinity
    let oldestKey = null
    for (const [key, entry] of this._cache) {
      if (entry.lastUsed < oldest) {
        oldest = entry.lastUsed
        oldestKey = key
      }
    }
    if (oldestKey === null) return -1
    const entry = this._cache.get(oldestKey)
    const idx = entry.slotIdx
    this._cache.delete(oldestKey)
    this._slots[idx] = null
    const col = idx % SLOTS_PER_ROW
    const row = Math.floor(idx / SLOTS_PER_ROW)
    this.ctx.clearRect(col * SLOT_W, row * SLOT_H, SLOT_W, SLOT_H)
    return idx
  }

  _growAtlas(minH) {
    let newH = this.atlasH
    while (newH < minH) newH *= 2
    if (newH === this.atlasH) return

    const oldData = this.ctx.getImageData(0, 0, this.atlasW, this.atlasH)
    this.atlasH = newH
    this.canvas.width = this.atlasW
    this.canvas.height = this.atlasH
    this.ctx = this.canvas.getContext('2d')
    this.ctx.imageSmoothingEnabled = false
    this.ctx.putImageData(oldData, 0, 0)

    this.texture.dispose()
    this.texture = new THREE.CanvasTexture(this.canvas)
    this.texture.magFilter = THREE.LinearFilter
    this.texture.minFilter = THREE.LinearFilter
    this.texture.generateMipmaps = false
    this.mesh.material.uniforms.uAtlas.value = this.texture
  }

  _renderSlot(slotIdx, text, color) {
    const col = slotIdx % SLOTS_PER_ROW
    const row = Math.floor(slotIdx / SLOTS_PER_ROW)
    const px = col * SLOT_W
    const py = row * SLOT_H

    this.ctx.clearRect(px, py, SLOT_W, SLOT_H)
    this.ctx.font = FONT
    this.ctx.textAlign = 'center'
    this.ctx.textBaseline = 'middle'

    const cx = px + SLOT_W / 2
    const cy = py + SLOT_H / 2

    // outline
    this.ctx.lineJoin = 'round'
    this.ctx.lineWidth = OUTLINE_SIZE * 2
    this.ctx.strokeStyle = OUTLINE_COLOR
    this.ctx.strokeText(text, cx, cy)

    // fill
    this.ctx.fillStyle = color
    this.ctx.fillText(text, cx, cy)

    this.texture.needsUpdate = true
  }

  _resolve(text, color) {
    const key = `${text}|${color}`
    const existing = this._cache.get(key)
    if (existing) {
      existing.lastUsed = this.client.time
      return existing.slotIdx
    }

    let slotIdx
    const maxSlots = SLOTS_PER_ROW * Math.floor(2048 / SLOT_H)
    if (this._nextSlot >= maxSlots && this._freeSlots.length === 0) {
      slotIdx = this._evictLRU()
      if (slotIdx === -1) slotIdx = this._allocSlot()
    } else {
      slotIdx = this._allocSlot()
    }

    this._slots[slotIdx] = key
    this._cache.set(key, { slotIdx, lastUsed: this.client.time })
    this._renderSlot(slotIdx, text, color)
    return slotIdx
  }

  _slotUV(slotIdx) {
    const col = slotIdx % SLOTS_PER_ROW
    const row = Math.floor(slotIdx / SLOTS_PER_ROW)
    const px = col * SLOT_W
    const py = row * SLOT_H
    return {
      u: px / this.atlasW,
      v: 1 - (py + SLOT_H) / this.atlasH,
      w: SLOT_W / this.atlasW,
      h: SLOT_H / this.atlasH,
    }
  }

  // ── public API ──

  /**
   * @param {object} [opts]
   * @param {number} [opts.x]
   * @param {number} [opts.y]
   * @param {number|string} [opts.text]
   * @param {string} [opts.color='#ffffff']
   * @param {number} [opts.scale=1]
   * @param {number} [opts.lifetime]
   * @param {string} [opts.style='arc'] - 'arc' for parabolic, 'rise' for straight up
   */
  show({ x, y, text, scale = 1, color = '#ffffff', lifetime = LIFETIME, style = 'arc' }) {
    if (this._count >= MAX_ACTIVE) return

    const slotIdx = this._resolve(text, color)
    const uv = this._slotUV(slotIdx)
    const idx = this._count

    let vx, vy, grav

    if (style === 'rise') {
      // old-school: straight up, no gravity
      vx = 0
      vy = RISE_SPEED
      grav = 0
    } else {
      // arc: random parabolic trajectory
      const angleDeg = LAUNCH_ANGLE_MIN + Math.random() * (LAUNCH_ANGLE_MAX - LAUNCH_ANGLE_MIN)
      const angleRad = (angleDeg * Math.PI) / 180
      const dir = Math.random() < 0.5 ? -1 : 1
      const speed = LAUNCH_SPEED * (0.8 + Math.random() * 0.4)
      vx = dir * Math.cos(angleRad) * speed
      vy = Math.sin(angleRad) * speed
      grav = GRAVITY
    }

    // write instance attributes once
    const i4 = idx * 4
    this._uvArr[i4] = uv.u
    this._uvArr[i4 + 1] = uv.v
    this._uvArr[i4 + 2] = uv.w
    this._uvArr[i4 + 3] = uv.h

    const i2 = idx * 2
    this._startPosArr[i2] = x + (Math.random() - 0.5) * SPREAD
    this._startPosArr[i2 + 1] = y

    this._startTimeArr[idx] = this.client.time
    this._scaleArr[idx] = scale

    this._velArr[i2] = vx
    this._velArr[i2 + 1] = vy
    this._gravArr[idx] = grav

    this._active.push({
      idx,
      startTime: this.client.time,
      lifetime,
    })

    this._count++
    this._dirty = true
  }

  // ── per-frame: uTime + expired cleanup ──

  update = (delta, time) => {
    this.mesh.material.uniforms.uTime.value = time

    // sweep expired particles
    let i = 0
    while (i < this._active.length) {
      const p = this._active[i]
      if (time - p.startTime >= p.lifetime) {
        this._removeAt(i, p.idx)
        this._dirty = true
      } else {
        i++
      }
    }

    if (this._dirty) {
      this._flushBuffers()
      this._dirty = false
    }

    if (this._active.length === 0) {
      this.mesh.count = 0
    }
  }

  // swap-remove: pack buffer by moving last instance into the hole
  _removeAt(activeIdx, bufIdx) {
    const last = this._count - 1

    if (bufIdx !== last) {
      // copy last → hole
      const i4d = bufIdx * 4
      const i4s = last * 4
      this._uvArr[i4d] = this._uvArr[i4s]
      this._uvArr[i4d + 1] = this._uvArr[i4s + 1]
      this._uvArr[i4d + 2] = this._uvArr[i4s + 2]
      this._uvArr[i4d + 3] = this._uvArr[i4s + 3]

      const i2d = bufIdx * 2
      const i2s = last * 2
      this._startPosArr[i2d] = this._startPosArr[i2s]
      this._startPosArr[i2d + 1] = this._startPosArr[i2s + 1]

      this._startTimeArr[bufIdx] = this._startTimeArr[last]
      this._scaleArr[bufIdx] = this._scaleArr[last]

      this._velArr[i2d] = this._velArr[i2s]
      this._velArr[i2d + 1] = this._velArr[i2s + 1]

      this._gravArr[bufIdx] = this._gravArr[last]

      // patch the moved entry's idx
      for (let j = 0; j < this._active.length; j++) {
        if (this._active[j].idx === last) {
          this._active[j].idx = bufIdx
          break
        }
      }
    }

    // swap-remove from _active array
    const lastActive = this._active.length - 1
    if (activeIdx !== lastActive) {
      this._active[activeIdx] = this._active[lastActive]
    }
    this._active.pop()
    this._count--
  }

  _flushBuffers() {
    this.mesh.count = this._count
    if (this._count > 0) {
      this.mesh.geometry.attributes.aUvRect.needsUpdate = true
      this.mesh.geometry.attributes.aStartPos.needsUpdate = true
      this.mesh.geometry.attributes.aStartTime.needsUpdate = true
      this.mesh.geometry.attributes.aScale.needsUpdate = true
      this.mesh.geometry.attributes.aVel.needsUpdate = true
      this.mesh.geometry.attributes.aGravity.needsUpdate = true
    }
  }

  destroy() {
    this.client.scene.remove(this.mesh)
    this.mesh.geometry.dispose()
    this.mesh.material.dispose()
    this.texture.dispose()
  }
}
